import { Megaphone } from 'lucide-react';
import { type ReactNode } from 'react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { useShiftReference } from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import {
    type HarnessScope,
    type KnownEventType,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { referenceFallbackLabel } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import {
    defineStandalone,
    type RegistryOccurrence,
    type SafeRenderContext,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { useViewerUserId } from '@apps/web/features/phoebe_chat_v3/ui/viewer_user_context';
import { formatDateWithWeekday, formatTime } from '@apps/web/lib/date_utils';
import {
    buildOutreachDisplayUnits,
    groupModeLabel,
} from '@apps/web/lib/outreach_shift_ordering';
import { useJoinedOutreach } from '@apps/web/swr_data_fetching/use_joined_outreach';
import { type components } from '@libraries/typescript/api_client';

const UNATTRIBUTED_KICKOFF_COPY = 'This outreach was kicked off.';

type JoinedOutreach = components['schemas']['JoinedOutreachRead'];

type ShiftGroupMode =
    components['schemas']['OutreachShiftGroupRead']['group_mode'];

type KickoffRow =
    | Readonly<{ readonly kind: 'shift'; readonly shiftId: string }>
    | Readonly<{
          readonly kind: 'group';
          readonly id: string;
          readonly shiftIds: ReadonlyArray<string>;
          readonly groupMode: ShiftGroupMode;
      }>;

const outreachRunnerKickoffPayloadSchema = z
    .object({
        type: z.literal('outreach_runner_kickoff').optional(),
        outreach_id: z.uuid(),
        shift_ids: z.array(z.uuid()),
        expires_at: z.iso.datetime({ offset: true }),
        instructions: z.string(),
    })
    .strict();

type OutreachRunnerKickoffPayload = z.infer<
    typeof outreachRunnerKickoffPayloadSchema
>;

export const outreachRunnerKickoffDefinitions = {
    outreach_runner_kickoff: defineStandalone('outreach_runner_kickoff', {
        disposition: 'render',
        schema: outreachRunnerKickoffPayloadSchema,
        malformed: {
            kind: 'placeholder',
            customerCopy: UNATTRIBUTED_KICKOFF_COPY,
        },
        evidence: null,
        group: null,
        render(payload, occurrence, context) {
            return (
                <OutreachRunnerKickoffLine
                    payload={payload}
                    occurrence={occurrence}
                    context={context}
                />
            );
        },
    }),
} as const;

/**
 * The kicked-off shifts as the outreach page lists them: the outreach's
 * display units (each multi-shift group first, then standalone shifts by
 * start time) restricted to the shifts this kick-off named. Until the
 * outreach has loaded they list flat, in kick-off order.
 */
function kickoffRows(
    shiftIds: ReadonlyArray<string>,
    outreach: JoinedOutreach | undefined,
): ReadonlyArray<KickoffRow> {
    if (outreach === undefined) {
        return shiftIds.map((shiftId) => ({ kind: 'shift', shiftId }));
    }
    const kickedOff = new Set(shiftIds);
    const units = buildOutreachDisplayUnits(
        outreach.shifts.filter((shift) => kickedOff.has(shift.id)),
        outreach.shift_groups,
    );
    const listed = new Set(units.flatMap((unit) => unit.shiftIds));
    return [
        ...units.map((unit): KickoffRow =>
            unit.kind === 'group'
                ? {
                      kind: 'group',
                      id: unit.id,
                      shiftIds: unit.shiftIds,
                      groupMode: unit.groupMode,
                  }
                : { kind: 'shift', shiftId: unit.id },
        ),
        // A shift removed from the outreach since the kick-off still counts
        // among the shifts it was kicked off for.
        ...shiftIds
            .filter((shiftId) => !listed.has(shiftId))
            .map((shiftId): KickoffRow => ({ kind: 'shift', shiftId })),
    ];
}

// The starter's name comes from the committed event's server-resolved
// attribution: the kickoff is enqueued with the starting user, and an
// auto-started outreach has no user, which reads as Phoebe's own doing.
function OutreachRunnerKickoffLine({
    payload,
    occurrence,
    context,
}: Readonly<{
    readonly payload: OutreachRunnerKickoffPayload;
    readonly occurrence: RegistryOccurrence<KnownEventType>;
    readonly context: SafeRenderContext;
}>): ReactNode {
    const viewerUserId = useViewerUserId();
    const sentBy = occurrence.committed?.sent_by ?? null;
    // The pending enqueue already knows who started the outreach, so the
    // viewer's own kickoff reads "You" before the committed event lands;
    // a collaborator stays unattributed until sent_by carries their name,
    // matching StopRequestLine's convention.
    const starterUserId =
        sentBy?.user_id ?? occurrence.pending?.enqueued_by_user_id ?? null;
    const starter =
        starterUserId !== null && starterUserId === viewerUserId
            ? 'You'
            : (sentBy?.display_name ?? 'Phoebe');
    // Groups live on the outreach, not in the payload, so chats kicked off
    // before this rendered show them too.
    const { data: outreach } = useJoinedOutreach(
        context.scope.organizationId,
        payload.outreach_id,
        { reason: 'v3-outreach-kickoff' },
        context.scope.mode,
    );
    const rows = kickoffRows(payload.shift_ids, outreach);
    const shiftCount = payload.shift_ids.length;
    return (
        <div>
            <EventLine glyph={<Megaphone />}>
                <span className="font-semibold">{starter}</span> kicked off this
                outreach for{' '}
                <span className="font-semibold">
                    {shiftCount === 1 ? '1 shift' : `${shiftCount} shifts`}
                </span>
            </EventLine>
            {rows.length > 0 && (
                <ul className="text-muted-foreground text-v3-chat-meta mt-1.5 grid list-none gap-[5px] pl-5">
                    {rows.map((row) =>
                        row.kind === 'shift' ? (
                            <KickoffShiftLine
                                key={row.shiftId}
                                scope={context.scope}
                                shiftId={row.shiftId}
                                organizationTimeZone={
                                    context.organizationTimeZone
                                }
                            />
                        ) : (
                            <li
                                key={row.id}
                                className="bg-muted -ml-2 flex items-start gap-3 rounded-md px-2 py-1.5 [&+li]:mt-[3px]"
                            >
                                <ul className="grid min-w-0 flex-1 list-none gap-[5px]">
                                    {row.shiftIds.map((shiftId) => (
                                        <KickoffShiftLine
                                            key={shiftId}
                                            scope={context.scope}
                                            shiftId={shiftId}
                                            organizationTimeZone={
                                                context.organizationTimeZone
                                            }
                                        />
                                    ))}
                                </ul>
                                <Badge
                                    variant="outline"
                                    className="bg-background text-muted-foreground px-1.5 py-0 text-[10px] leading-4"
                                >
                                    {groupModeLabel(row.groupMode)}
                                </Badge>
                            </li>
                        ),
                    )}
                </ul>
            )}
        </div>
    );
}

function KickoffShiftLine({
    scope,
    shiftId,
    organizationTimeZone,
}: Readonly<{
    readonly scope: HarnessScope;
    readonly shiftId: string;
    readonly organizationTimeZone: string;
}>): ReactNode {
    const shift = useShiftReference(scope, shiftId);
    // The fallback keeps the row while the batched lookup resolves, so the
    // opening block never changes height once drawn.
    if (shift === null) {
        return <li>{referenceFallbackLabel('shift')}</li>;
    }
    return (
        <li>
            <span className="text-foreground/75 font-semibold">
                {shift.patientName}
            </span>
            {' · '}
            {formatDateWithWeekday(shift.shiftStartTime, organizationTimeZone)}
            {' · '}
            {formatTime(shift.shiftStartTime, organizationTimeZone)}
            {' – '}
            {formatTime(shift.shiftEndTime, organizationTimeZone)}
        </li>
    );
}

export const outreachRunnerKickoffContractFixtures = {
    valid: {
        type: 'outreach_runner_kickoff',
        outreach_id: '00000000-0000-4000-8000-000000000001',
        shift_ids: [
            '00000000-0000-4000-8000-000000000002',
            '00000000-0000-4000-8000-000000000003',
        ],
        expires_at: '2026-09-10T12:00:00Z',
        instructions: 'Re-read the outreach before acting.',
    },
    malformed: {
        type: 'outreach_runner_kickoff',
        outreach_id: '00000000-0000-4000-8000-000000000001',
        shift_ids: ['not-a-uuid'],
        expires_at: '2026-09-10T12:00:00Z',
        instructions: 'Re-read the outreach before acting.',
    },
} as const;
